import { all, takeLatest, put, call, select, delay, take, race, fork } from "redux-saga/effects";
import { toast } from 'react-hot-toast';
import organizationsSearchSlice, { filterOrganizations, loadOrganizationUsers, loadOrganizations, selectOrganizationsSearchOrganizationsGridParams, selectOrganizationsSearchSelectedOrganizationUsersGridParams, viewOrganization } from "./slice";
import { IConfiguration } from "../../../shared/types";
import { selectConfig } from "../../../shared/redux/slice";
import { selectIdToken, unauthorized } from "../../../auth/redux/slice";
import { SimplyTestApi } from "../../../api/simplytest";
import { AxiosResponse } from "axios";
import { OrganizationsResponse, OrganizationsSearchResponse, UsersResponse } from "../../../api/types";
import { OrganizationGridParams } from "../types";

export abstract class OrganizationSagas {
    public static* run() {
        try {
            yield all([
                fork(OrganizationSagas.debounceFilterOrgs),
                takeLatest(loadOrganizations, OrganizationSagas.loadOrganizations),
                takeLatest(loadOrganizationUsers, OrganizationSagas.loadOrganizationUsers),
                takeLatest(viewOrganization, OrganizationSagas.viewOrganization),
            ]);
        }
        catch(e: any){
            toast.error('Unable to register Organizations module.');
            console.log(`[OrganizationSagas:run] the following exception has occurred`, e);
        }
    }

    private static* viewOrganization(action: ReturnType<typeof viewOrganization>){
        const { payload: { org, rowIndex }} = action;
        try {
            yield put(organizationsSearchSlice.actions.setSelectedOrganization(org));
            yield put(organizationsSearchSlice.actions.setSelectedOrganizationRowIndex(rowIndex));
        }
        catch(e: any){
            toast.error('Unable to load organization.');
            console.log(`[OrganizationSagas:viewOrganization] the following exception has occurred`, e);
        }
        finally {
            yield put(organizationsSearchSlice.actions.setState('READY'));
        }
    }

    private static* loadOrganizations(action: ReturnType<typeof loadOrganizations>){
        try {
            yield put(organizationsSearchSlice.actions.setState('BUSY'));
            const gridParams: OrganizationGridParams = yield select(selectOrganizationsSearchOrganizationsGridParams);
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orgsResponse: AxiosResponse<OrganizationsSearchResponse> = yield call(
                {
                    context: client,
                    fn: client.searchOrganizations
                },
                {
                    ...gridParams
                }
            );

            if(orgsResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(orgsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            const { data: { organizations, previousPage, nextPage } } = orgsResponse;
            yield put(organizationsSearchSlice.actions.setOrganizations(organizations));
        }
        catch(e: any){
            toast.error('Unable to load organizations.');
            console.log(`[OrganizationSagas:loadOrganizations] the following exception has occurred`, e);
        }
        finally {
            yield put(organizationsSearchSlice.actions.setState('READY'));
        }
    }

    private static* loadOrganizationUsers(action: ReturnType<typeof loadOrganizationUsers>){
        const { payload: orgId } = action;
        try {
            if(orgId === undefined){
                yield put(organizationsSearchSlice.actions.setOrganizationUsers([]));
                yield put(organizationsSearchSlice.actions.setOrganizationUsersState('READY'));
                return;
            }
            yield put(organizationsSearchSlice.actions.setOrganizationUsersState('BUSY'));

            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            
            const client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orgUsersGridParams = yield select(selectOrganizationsSearchSelectedOrganizationUsersGridParams);

            const orgUsersResponse: AxiosResponse<UsersResponse> = yield call(
                {
                    context: client,
                    fn: client.getOrganizationUsers
                },
                orgId,
                orgUsersGridParams,
            );

            if(orgUsersResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;     
            }
            
            if(orgUsersResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(organizationsSearchSlice.actions.setOrganizationUsers(orgUsersResponse.data));
        }
        catch(e: any){
            toast.error('Unable to load organization users.');
            console.log(`[OrganizationSagas:loadOrganizationUsers] the following exception has occurred`, e);
        }
        finally {
            yield put(organizationsSearchSlice.actions.setOrganizationUsersState('READY'));
        }
    }

    private static* debounceFilterOrgs(){
        try {

            /*
                over-engineered but, debounce the api calls for searching
                debounces for 1s from the last search and cancels the debounce if a loadOrders action
                gets sent through the middleware.
            */
            while(true){
                yield take(filterOrganizations);
                const [_, runFilter] = yield race([
                    take(loadOrganizations),
                    delay(1000),
                ]);

                if(runFilter){
                    yield put(loadOrganizations());
                }
            }
        }
        catch(e: any){
            console.log(e);
        }
    }
}